import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaPlus, FaTimes, FaCheckCircle, FaTrash, FaIdCard } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const ProviderTypes = () => {
	const { token } = useAuth();
	const [providerTypes, setProviderTypes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingType, setEditingType] = useState(null);
	const [formData, setFormData] = useState({
		name: '',
		description: '',
		is_active: true,
	});

	// Load provider types
	const loadProviderTypes = useCallback(async () => {
		if (!token) return;
		try {
			setLoading(true);
			setError(null);
			const response = await apiCall('/providers/provider-types/', {
				method: 'GET'
			}, token);
			if (response.ok) {
				const data = await response.json();
				const items = data.results || data;
				setProviderTypes(Array.isArray(items) ? items : []);
			}
		} catch (err) {
			console.error('Error loading provider types:', err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadProviderTypes();
	}, [loadProviderTypes]);

	const handleAddType = () => {
		setEditingType(null);
		setFormData({
			name: '',
			description: '',
			is_active: true,
		});
		setIsModalOpen(true);
	};

	const handleEditType = (type) => {
		setEditingType(type);
		setFormData({
			name: type.name || '',
			description: type.description || '',
			is_active: type.is_active,
		});
		setIsModalOpen(true);
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!token) return;
		try {
			setError(null);
			const url = editingType
				? `/providers/provider-types/${editingType.id}/`
				: '/providers/provider-types/';
			
			const method = editingType ? 'PATCH' : 'POST';
			
			const response = await apiCall(url, {
				method,
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData),
			}, token);

			if (response.ok) {
				setIsModalOpen(false);
				setEditingType(null);
				loadProviderTypes();
			}
		} catch (err) {
			console.error('Error saving provider type:', err);
			setError(err.message);
		}
	};

	const handleDeactivateType = async (id) => {
		if (!window.confirm('Are you sure you want to deactivate this provider type?')) {
			return;
		}
		if (!token) return;
		try {
			setError(null);
			const response = await apiCall(`/providers/provider-types/${id}/`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ is_active: false }),
			}, token);
			if (response.ok) {
				loadProviderTypes();
			}
		} catch (err) {
			console.error('Error deactivating provider type:', err);
			setError(err.message);
		}
	};

	const handleReactivateType = async (id) => {
		if (!window.confirm('Are you sure you want to reactivate this provider type?')) {
			return;
		}
		if (!token) return;
		try {
			setError(null);
			const response = await apiCall(`/providers/provider-types/${id}/`, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ is_active: true }),
			}, token);
			if (response.ok) {
				loadProviderTypes();
			}
		} catch (err) {
			console.error('Error reactivating provider type:', err);
			setError(err.message);
		}
	};

	return (
		<div className="p-6">
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaIdCard className="text-primary" />
					Provider Types
				</h1>
				<button
					className="btn btn-primary"
					onClick={handleAddType}
				>
					<FaPlus className="mr-2" /> Add Provider Type
				</button>
			</div>

			{error && (
				<div className="alert alert-error mb-4">
					<span>{error}</span>
				</div>
			)}

			{loading ? (
				<div className="flex justify-center items-center py-12">
					<div className="loading loading-spinner loading-lg"></div>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table className="table table-zebra bg-base-100/50">
						<thead>
							<tr>
								<th>Name</th>
								<th>Description</th>
								<th>Status</th>
								<th className="w-32">Actions</th>
							</tr>
						</thead>
						<tbody>
							{providerTypes.length === 0 ? (
								<tr>
									<td colSpan="4" className="text-center py-8 text-base-content/60">
										No provider types found. Create your first provider type to get started.
									</td>
								</tr>
							) : (
								providerTypes.map((type) => (
									<tr key={type.id}>
										<td className="font-semibold">{type.name}</td>
										<td className="text-sm">{type.description || '-'}</td>
										<td>
											<span className={`badge ${type.is_active ? 'badge-success' : 'badge-error'}`}>
												{type.is_active ? 'Active' : 'Inactive'}
											</span>
										</td>
										<td>
											<div className="flex gap-2">
												<button
													className="btn btn-ghost btn-xs"
													onClick={() => handleEditType(type)}
													title="Edit"
												>
													<FaEdit className="w-4 h-4" />
												</button>
												{type.is_active ? (
													<button
														className="btn btn-ghost btn-xs text-error"
														onClick={() => handleDeactivateType(type.id)}
														title="Deactivate"
													>
														<FaTrash className="w-4 h-4" />
													</button>
												) : (
													<button
														className="btn btn-ghost btn-xs text-success"
														onClick={() => handleReactivateType(type.id)}
														title="Reactivate"
													>
														<FaCheckCircle className="w-4 h-4" />
													</button>
												)}
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}

			{isModalOpen && (
				<div className="modal modal-open">
					<div className="modal-box max-w-2xl">
						<div className="flex justify-between items-center mb-4">
							<h3 className="font-bold text-lg">
								{editingType ? 'Edit Provider Type' : 'Create New Provider Type'}
							</h3>
							<button
								className="btn btn-ghost btn-sm btn-circle"
								onClick={() => setIsModalOpen(false)}
							>
								<FaTimes />
							</button>
						</div>

						<form onSubmit={handleSubmit}>
							<div className="grid grid-cols-1 gap-4">
								<div className="form-control">
									<label className="label">
										<span className="label-text">Provider Type Name *</span>
									</label>
									<input
										type="text"
										name="name"
										value={formData.name}
										onChange={handleInputChange}
										className="input input-bordered"
										placeholder="e.g., General Practitioner, Orthopaedic Surgeon, Neurosurgeon/Spine Surgeon"
										required
									/>
									<label className="label">
										<span className="label-text-alt text-base-content/60">
											Specific specialty or role name
										</span>
									</label>
								</div>

								<div className="form-control">
									<label className="label">
										<span className="label-text">Description</span>
									</label>
									<textarea
										name="description"
										value={formData.description}
										onChange={handleInputChange}
										className="textarea textarea-bordered"
										placeholder="Additional details about this provider type..."
										rows="3"
									></textarea>
								</div>

								<div className="form-control">
									<label className="label cursor-pointer justify-start gap-2">
										<input
											type="checkbox"
											name="is_active"
											checked={formData.is_active}
											onChange={handleInputChange}
											className="checkbox checkbox-primary"
										/>
										<span className="label-text">Active</span>
									</label>
								</div>
							</div>

							<div className="modal-action">
								<button
									type="button"
									className="btn btn-ghost"
									onClick={() => setIsModalOpen(false)}
								>
									Cancel
								</button>
								<button type="submit" className="btn btn-primary">
									{editingType ? 'Update Provider Type' : 'Create Provider Type'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default ProviderTypes;
